import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, TreePine, Waves, Recycle, PlugZap, HeartHandshake,
  Award, FileDown, LogIn, Leaf, BatteryCharging, Share2,
} from "lucide-react";
import { Header } from "../sections/Header";
import { Seo } from "../components/Seo";
import { EarthGrove } from "../components/EarthGrove";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const KG_TO_LBS = 2.20462;

const fmt = (n, d = 0) =>
  typeof n === "number"
    ? n.toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d })
    : "—";

/** Big-number counter that ticks up live using the API's rate_per_hour. */
const LiveCounter = ({ id, base, ratePerHour = 0, decimals = 0, label, unit }) => {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (!ratePerHour) return undefined;
    const t = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(t);
  }, [ratePerHour]);
  const value = (base || 0) + (ratePerHour / 3600) * elapsed;
  return (
    <div
      data-testid={`earth-live-counter-${id}`}
      className="rounded-2xl p-5 sm:p-6"
      style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(57,255,20,0.14)" }}
    >
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 mb-2">{label}</div>
      <div className="text-white text-3xl sm:text-4xl font-bold tabular-nums">
        {fmt(value, decimals)}
        <span className="ml-1.5 text-xs font-mono font-normal text-zinc-500">{unit}</span>
      </div>
    </div>
  );
};

const ImpactShareButton = ({ vin }) => {
  const [busy, setBusy] = useState(false);
  const share = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await fetch(`${API_BASE}/api/earth/share-card/${encodeURIComponent(vin)}.png`);
      if (!r.ok) throw new Error("card failed");
      const blob = await r.blob();
      const file = new File([blob], `eviq_earth_impact_${String(vin).slice(-4)}.png`, { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: "My EVIQ Earth Impact",
          text: "My Tesla battery is planting a grove — live impact via EVIQ. eviq.pro/earth",
        });
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = file.name;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch {
      /* user cancelled share or card unavailable */
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      type="button"
      onClick={share}
      disabled={busy}
      data-testid="earth-share-card-btn"
      className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.16em] text-black font-bold transition-opacity hover:opacity-90 disabled:opacity-50"
      style={{ background: "linear-gradient(90deg,#39FF14,#00E5FF)" }}
    >
      <Share2 className="w-3 h-3" strokeWidth={2.5} />
      {busy ? "Preparing…" : "Share my impact"}
    </button>
  );
};

const ImpactTile = ({ id, icon: Icon, value, label }) => (
  <div
    data-testid={`earth-impact-tile-${id}`}
    className="rounded-2xl p-5 flex flex-col items-center text-center gap-2"
    style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}
  >
    <Icon className="w-5 h-5" style={{ color: "#39FF14" }} aria-hidden="true" />
    <div className="text-white text-2xl sm:text-3xl font-bold font-mono tabular-nums">{value}</div>
    <div className="text-zinc-400 text-xs">{label}</div>
  </div>
);

const FUNDS = [
  { icon: TreePine, title: "Forest restoration", body: "Native-species planting in verified reforestation projects." },
  { icon: Waves, title: "Ocean cleanup", body: "Removing oceanbound plastic before it fragments." },
  { icon: Recycle, title: "Battery recycling", body: "Closed-loop recovery of lithium, nickel and cobalt." },
  { icon: HeartHandshake, title: "Veteran clean-energy careers", body: "Training veterans for grid + storage jobs." },
  { icon: PlugZap, title: "Community charging", body: "Public L2 chargers in underserved neighborhoods." },
];

/**
 * EarthDashboard — the native EVIQ Earth Initiative page at /earth.
 * Greets the actual signed-in user (founding-member profile or Tesla
 * account name) and renders their live impact numbers from our own API —
 * replacing the old battery-legacy iframe that showed demo data.
 */
export default function EarthDashboard() {
  const [founder, setFounder] = useState(null);
  const [signedIn, setSignedIn] = useState(false);
  const [name, setName] = useState(null);
  const [personal, setPersonal] = useState(null);
  const [grove, setGrove] = useState(null);
  const [platform, setPlatform] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Adopt the OAuth return (?tesla=connected&session_id=…) before reading
      // localStorage — the sign-in pill round-trips through Tesla back to /earth.
      const params = new URLSearchParams(window.location.search);
      if (params.get("tesla") === "connected" && params.get("session_id")) {
        window.localStorage.setItem("eviq.tesla_session_id", params.get("session_id"));
        window.history.replaceState({}, "", window.location.pathname);
      }
      const sessionId = window.localStorage.getItem("eviq.tesla_session_id");
      let fm = null;
      try {
        fm = JSON.parse(window.localStorage.getItem("eviq.founding_member") || "null");
      } catch { fm = null; }
      if (!cancelled) {
        setFounder(fm);
        setSignedIn(Boolean(sessionId || fm));
        if (fm?.name) setName(fm.name);
      }

      let vin = null;
      if (sessionId) {
        const [vehiclesRes, meRes] = await Promise.allSettled([
          fetch(`${API_BASE}/api/tesla/vehicles?session_id=${encodeURIComponent(sessionId)}`),
          fetch(`${API_BASE}/api/tesla/me?session_id=${encodeURIComponent(sessionId)}`),
        ]);
        if (vehiclesRes.status === "fulfilled" && vehiclesRes.value.ok) {
          const j = await vehiclesRes.value.json();
          const selectedId = window.localStorage.getItem("eviq.tesla_selected_vehicle_id");
          const list = j.vehicles || [];
          const v = list.find((x) => String(x.id) === String(selectedId)) || list[0];
          vin = v?.vin || null;
        }
        if (meRes.status === "fulfilled" && meRes.value.ok) {
          const me = await meRes.value.json();
          if (!cancelled && !fm?.name && me.full_name) setName(me.full_name);
        }
      }

      const [mine, groveRes, agg] = await Promise.all([
        vin
          ? fetch(`${API_BASE}/api/earth/impact/${encodeURIComponent(vin)}`).then((r) => (r.ok ? r.json() : null)).catch(() => null)
          : Promise.resolve(null),
        vin
          ? fetch(`${API_BASE}/api/earth/milestones/${encodeURIComponent(vin)}`).then((r) => (r.ok ? r.json() : null)).catch(() => null)
          : Promise.resolve(null),
        fetch(`${API_BASE}/api/earth/impact/aggregate`).then((r) => (r.ok ? r.json() : null)).catch(() => null),
      ]);
      if (cancelled) return;
      setPersonal(mine?.exists ? mine : null);
      setGrove(groveRes?.exists ? groveRes : null);
      setPlatform(agg);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  const firstName = name ? name.trim().split(/\s+/)[0] : null;
  const eq = personal?.equivalences;
  const rates = platform?.rate_per_hour || {};

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "#000" }}>
      <Seo
        title="Earth Initiative"
        description="Every EVIQ membership funds forest restoration, ocean cleanup and battery recycling. See the live impact of packs kept in service."
        canonical="https://eviq.pro/earth"
      />
      <Header />
      <main className="flex-1 pt-16" data-testid="earth-dashboard">
        {/* ---- Hero ---- */}
        <section className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8 pt-12 sm:pt-16 pb-10">
          <div className="font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.26em] mb-4" style={{ color: "#39FF14" }}>
            Welcome to EVIQ Earth Initiative
          </div>
          <h1 data-testid="earth-hero-greeting" className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.08]">
            {firstName ? (
              <>Hello, <em className="font-serif italic" style={{ color: "#39FF14" }}>{firstName}</em>.</>
            ) : (
              <>Hello.</>
            )}
            <br />
            You're already changing the world.
          </h1>
          <p className="mt-5 max-w-2xl text-zinc-400 text-base leading-relaxed">
            Every EVIQ membership quietly funds forest restoration, ocean cleanup, battery recycling,
            veteran clean-energy careers, and community charging. No donation prompts. Just outcomes.
          </p>
          <div className="mt-5 flex flex-wrap items-center gap-3">
            {founder?.badge && (
              <span
                data-testid="earth-founder-badge"
                className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 font-mono text-[11px] uppercase tracking-[0.14em] text-black font-bold"
                style={{ background: "linear-gradient(90deg,#39FF14,#00E5FF)" }}
              >
                <Award className="w-3.5 h-3.5" /> Founding Member {founder.badge}
              </span>
            )}
            {!signedIn && !loading && (
              <a
                href={`${API_BASE}/api/tesla/authorize?return_to=earth`}
                data-testid="earth-signin-pill"
                className="inline-flex items-center gap-2 rounded-full px-4 py-2 font-mono text-[11px] uppercase tracking-[0.16em]"
                style={{ border: "1px solid rgba(0,229,255,0.4)", color: "#00E5FF" }}
              >
                <LogIn className="w-3.5 h-3.5" /> Sign in on EVIQ for your personal impact
              </a>
            )}
          </div>
        </section>

        {/* ---- Personal impact ---- */}
        <section className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8 pb-12">
          <div
            className="rounded-3xl p-6 sm:p-8"
            style={{ background: "rgba(6,14,6,0.9)", border: "1px solid rgba(57,255,20,0.18)" }}
          >
            <div className="flex items-center justify-between flex-wrap gap-2 mb-5">
              <div className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em]" style={{ color: "#39FF14" }}>
                <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: "#39FF14" }} />
                {personal ? "Your live impact" : "Community impact · live"}
              </div>
              {personal?.vin && (
                <span className="inline-flex items-center gap-3">
                  <span className="font-mono text-[10px] text-zinc-500 tracking-wider">VIN ···{String(personal.vin).slice(-6)}</span>
                  <ImpactShareButton vin={personal.vin} />
                </span>
              )}
            </div>

            {personal && eq ? (
              <>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4" data-testid="earth-personal-grid">
                  <ImpactTile id="trees" icon={TreePine} value={fmt(eq.trees_planted_equivalent, 1)} label="Trees planted · equivalent" />
                  <ImpactTile id="plastic" icon={Waves} value={fmt(eq.ocean_plastic_kg_equivalent * KG_TO_LBS)} label="Lbs ocean plastic · equivalent" />
                  <ImpactTile id="co2" icon={Leaf} value={`${fmt(personal.co2_deferred_kg)} kg`} label="CO₂ deferred" />
                  <ImpactTile id="packs" icon={BatteryCharging} value={eq.battery_packs_not_mined} label="Battery packs not mined" />
                </div>
                <div className="mt-4 rounded-xl px-4 py-3 flex flex-wrap gap-x-6 gap-y-1 text-xs text-zinc-400 font-mono" style={{ background: "rgba(255,255,255,0.03)" }}>
                  <span>{fmt(personal.months_in_service, 1)} months in service</span>
                  <span>{fmt(personal.cells_kept_in_service)} cells kept working</span>
                  <span>{fmt(personal.gasoline_miles_avoided)} gasoline miles avoided</span>
                  <span>{fmt(personal.water_preserved_l)} L freshwater preserved</span>
                </div>
              </>
            ) : signedIn && !loading ? (
              <div className="flex flex-col sm:flex-row sm:items-center gap-4" data-testid="earth-personalize-cta">
                <p className="text-zinc-400 text-sm flex-1">
                  Your account is connected but we don't have a Battery Passport for your VIN yet —
                  generate one in the app to unlock your personal numbers.
                </p>
                <Link
                  to="/app"
                  className="shrink-0 inline-flex items-center rounded-full px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.16em] text-black font-bold"
                  style={{ background: "linear-gradient(90deg,#39FF14,#00E5FF)" }}
                >
                  Generate my passport →
                </Link>
              </div>
            ) : (
              <p className="text-zinc-500 text-sm font-mono">{loading ? "Loading live numbers…" : "Community totals below."}</p>
            )}

            {signedIn && (
              <div className="mt-5 rounded-xl px-4 py-3" style={{ background: "rgba(57,255,20,0.06)", border: "1px solid rgba(57,255,20,0.15)" }}>
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 mr-2">Signup impact</span>
                <span className="text-sm text-zinc-200">
                  EVIQ planted <span style={{ color: "#39FF14" }}>1 tree</span> the moment you joined. Every milestone plants more.
                </span>
              </div>
            )}
          </div>
        </section>

        <EarthGrove grove={grove} />

        {/* ---- Community live counters ---- */}
        <section className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8 pb-12">
          <h2 className="text-white text-base md:text-lg font-bold mb-4">
            What EVIQ owners preserved — together
            <span className="ml-3 font-mono text-[10px] uppercase tracking-[0.2em] align-middle" style={{ color: "#00E5FF" }}>
              {platform ? `${fmt(platform.vins_tracked)} packs tracked` : ""}
            </span>
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
            <LiveCounter id="co2" base={platform?.co2_deferred_kg} ratePerHour={rates.co2_kg_per_hour} decimals={1} label="CO₂ deferred" unit="kg" />
            <LiveCounter id="water" base={platform?.water_preserved_l} ratePerHour={rates.water_l_per_hour} decimals={0} label="Freshwater preserved" unit="L" />
            <LiveCounter id="cells" base={platform?.cells_kept_in_service} ratePerHour={rates.cells_per_hour} decimals={0} label="Cells kept in service" unit="cells" />
          </div>
        </section>

        {/* ---- What membership funds ---- */}
        <section className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8 pb-12">
          <h2 className="text-white text-base md:text-lg font-bold mb-4">What your membership funds</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            {FUNDS.map((f) => (
              <div key={f.title} className="rounded-2xl p-4" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)" }}>
                <f.icon className="w-5 h-5 mb-3" style={{ color: "#00E5FF" }} aria-hidden="true" />
                <div className="text-white text-sm font-semibold mb-1">{f.title}</div>
                <div className="text-zinc-500 text-xs leading-relaxed">{f.body}</div>
              </div>
            ))}
          </div>
        </section>

        {/* ---- Report + methodology ---- */}
        <section className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8 pb-20">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 rounded-2xl p-5" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}>
            <div className="flex-1">
              <div className="text-white text-sm font-semibold">EVIQ Earth Report</div>
              <div className="text-zinc-500 text-xs mt-1">
                Print-quality PDF of the platform's aggregated impact — every number re-computable from the public API.
              </div>
            </div>
            <a
              href={`${API_BASE}/api/earth/annual-report.pdf`}
              data-testid="earth-annual-report-btn"
              className="shrink-0 inline-flex items-center gap-2 rounded-full px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.16em]"
              style={{ border: "1px solid rgba(57,255,20,0.4)", color: "#39FF14" }}
            >
              <FileDown className="w-4 h-4" /> Download PDF
            </a>
          </div>
          <p className="mt-6 font-mono text-[10px] leading-relaxed text-zinc-600">
            Methodology · Embodied CO₂ from Argonne GREET 2023 (75 kg CO₂e/kWh). Freshwater from Transport &amp; Environment 2023
            (500,000 L/tonne Li). Tree equivalence: 21 kg CO₂/tree-year (EPA). Plastic equivalence: 83 g CO₂e per 500 ml PET bottle.
            Baseline pack life 96 months. Equivalences are climate-benefit conversions, not direct purchases.
          </p>
        </section>

        <Link
          to="/app"
          data-testid="earth-back-to-app-pill"
          aria-label="Back to the EVIQ Battery App"
          className="fixed z-50 bottom-4 left-4 sm:bottom-6 sm:left-6 inline-flex items-center gap-2 rounded-full pl-2.5 pr-4 py-2 sm:pl-3 sm:pr-5 sm:py-2.5 font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.22em]"
          style={{
            background: "rgba(10,10,10,0.92)",
            border: "1px solid rgba(57,255,20,0.35)",
            color: "#ffffff",
            backdropFilter: "blur(12px)",
            boxShadow: "0 8px 24px rgba(0,0,0,0.45), 0 0 18px rgba(57,255,20,0.10)",
          }}
        >
          <span className="inline-flex items-center justify-center w-4 h-4 rounded-full" style={{ background: "rgba(57,255,20,0.14)", border: "1px solid rgba(57,255,20,0.45)" }}>
            <ArrowLeft className="w-2.5 h-2.5" style={{ color: "#39FF14" }} strokeWidth={2.6} />
          </span>
          EVIQ Battery App
        </Link>
      </main>
    </div>
  );
}
